import { useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// API helper for progress
const progressApi = async (method = 'GET', data = null) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  };

  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(`${API_URL}/api/progress`, options);
    if (!response.ok) throw new Error('API request failed');
    return await response.json();
  } catch (error) {
    console.error('Progress API error:', error);
    return null;
  }
};

// Hook to sync progress with backend
export function useProgressSync() {
  const syncInProgress = useRef(false);
  const pendingSync = useRef(null);

  // Load progress from server
  const loadProgress = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    const progress = await progressApi('GET');
    if (progress) {
      // Update localStorage with server data
      if (progress.completed_levels) {
        localStorage.setItem('fruitCrush_completedLevels', JSON.stringify(progress.completed_levels));
      }
      if (progress.level_scores) {
        localStorage.setItem('fruitCrush_levelScores', JSON.stringify(progress.level_scores));
      }
      if (progress.total_xp !== undefined) {
        // Store totalXP in the format useXP.js expects
        localStorage.setItem('fruitCrush_totalXP', progress.total_xp.toString());
      }
      if (progress.lives !== undefined) {
        localStorage.setItem('fruitCrush_lives', progress.lives.toString());
      }
      if (progress.last_life_lost) {
        localStorage.setItem('fruitCrush_lastLifeLost', progress.last_life_lost);
      } else {
        localStorage.removeItem('fruitCrush_lastLifeLost');
      }
      if (progress.stats) {
        localStorage.setItem('fruitCrush_stats', JSON.stringify(progress.stats));
      }
      if (progress.claimed_challenges) {
        localStorage.setItem('fruitCrush_claimedChallenges', JSON.stringify(progress.claimed_challenges));
      }
    }
    return progress;
  }, []);

  // Save progress to server
  const saveProgress = useCallback(async (data) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    // Convert from localStorage format to API format
    const apiData = {};
    
    if (data.completedLevels !== undefined) {
      apiData.completed_levels = data.completedLevels;
    }
    if (data.levelScores !== undefined) {
      apiData.level_scores = data.levelScores;
    }
    if (data.totalXP !== undefined) {
      apiData.total_xp = data.totalXP;
    }
    if (data.prestige !== undefined) {
      apiData.prestige = data.prestige;
    }
    if (data.lives !== undefined) {
      apiData.lives = data.lives;
    }
    if (data.lastLifeLost !== undefined) {
      apiData.last_life_lost = data.lastLifeLost;
    }
    if (data.stats !== undefined) {
      apiData.stats = data.stats;
    }
    if (data.claimedChallenges !== undefined) {
      apiData.claimed_challenges = data.claimedChallenges;
    }

    // Debounce sync
    if (syncInProgress.current) {
      pendingSync.current = apiData;
      return;
    }

    syncInProgress.current = true;
    await progressApi('PUT', apiData);
    syncInProgress.current = false;

    // Process any pending sync
    if (pendingSync.current) {
      const pending = pendingSync.current;
      pendingSync.current = null;
      await saveProgress(pending);
    }
  }, []);

  // Sync all current localStorage to server
  const syncAllProgress = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    const completedLevels = JSON.parse(localStorage.getItem('fruitCrush_completedLevels') || '[]');
    const levelScores = JSON.parse(localStorage.getItem('fruitCrush_levelScores') || '{}');
    // Use the correct localStorage key for totalXP
    const totalXP = parseInt(localStorage.getItem('fruitCrush_totalXP') || '0', 10);
    const lives = parseInt(localStorage.getItem('fruitCrush_lives') || '5', 10);
    const lastLifeLost = localStorage.getItem('fruitCrush_lastLifeLost');
    const stats = JSON.parse(localStorage.getItem('fruitCrush_stats') || '{}');
    const claimedChallenges = JSON.parse(localStorage.getItem('fruitCrush_claimedChallenges') || '[]');

    // Only sync if we have meaningful data to prevent overwriting server data with zeros
    if (completedLevels.length > 0 || totalXP > 0) {
      await saveProgress({
        completedLevels,
        levelScores,
        totalXP,
        lives,
        lastLifeLost,
        stats,
        claimedChallenges
      });
    }
  }, [saveProgress]);

  return {
    loadProgress,
    saveProgress,
    syncAllProgress
  };
}

// Helper to check if user is synced
export function isSyncEnabled() {
  return !!localStorage.getItem(TOKEN_KEY);
}
